"use client";

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DailyForm } from "@/components/daily-form";
import { DatePager } from "@/components/date-pager";
import { StatusScreen } from "@/components/status-screen";
import { TodayBoard } from "@/components/today-board";
import { useMeContext, useMeActions } from "@/components/me-provider";
import { useLiveReload } from "@/components/live-provider";
import { api } from "@/lib/api";
import type { TodayResponse } from "@/lib/types";

export function HomeView({ initialToday, active = true }: { initialToday?: TodayResponse; active?: boolean }) {
  const me = useMeContext();
  const { refreshMe } = useMeActions();
  const [payload, setPayload] = useState<TodayResponse | null>(initialToday ?? null);
  const [loading, setLoading] = useState(!initialToday);

  async function load(date?: string) {
    const query = date ? `?date=${encodeURIComponent(date)}` : "";
    const data = await api<TodayResponse>(`/api/today${query}`);
    setPayload(data);
    setLoading(false);
  }

  useEffect(() => {
    void load().catch((error) => {
      toast.error(error instanceof Error ? error.message : "Не вдалося завантажити день.");
      setLoading(false);
    });
  }, []);

  useLiveReload("today", () => load(payload?.date), active);

  if (me.user.role === "pending") {
    return (
      <StatusScreen
        title="Адміністратор ще не підтвердив доступ"
        text="Напишіть адміністратору класу. Він призначить роль і, якщо ви батьки, прив’яже до дитини."
      />
    );
  }

  if (loading && !payload) {
    return <StatusScreen loading title="Сьогодні" />;
  }

  if (!payload) {
    return (
      <StatusScreen
        title="Немає навчального дня"
        text={me.today.offReason ?? "Відмітки зараз не потрібні."}
      />
    );
  }

  return (
    <div className="space-y-6 wide:grid wide:grid-cols-2 wide:items-start wide:gap-6 wide:space-y-0">
      <div className="wide:col-span-2">
        <DatePager
        label={payload.label}
        hint={
          payload.eveningOpen
            ? "Відмітка вже відкрита"
            : payload.editable
              ? null
              : "Історія"
        }
        prevDate={payload.prevDate}
        nextDate={payload.nextDate}
        onChange={(date) => {
          void load(date).catch((error) => toast.error(error instanceof Error ? error.message : "Не вдалося відкрити день."));
        }}
      />
      </div>
      {payload.editable && me.children.length > 0 ? (
        <section>
          <DailyForm
            me={me}
            today={payload}
            onSaved={() => {
              void load(payload.date);
              void refreshMe();
            }}
          />
        </section>
      ) : null}
      <div className={payload.editable && me.children.length > 0 ? undefined : "wide:col-span-2"}>
        <TodayBoard
          me={me}
          today={payload}
          active={active}
          onChanged={() => {
            void load(payload.date);
            void refreshMe();
          }}
        />
      </div>
    </div>
  );
}
